import { useEffect, useRef, useState } from "react";
import { Upload, FileSpreadsheet, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError, exportUrl } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import InfoTip from "../components/InfoTip";
import StatusBadge from "../components/StatusBadge";

const STEPS = ["Fichier", "Analyse", "Mapping", "Apercu", "Import"];

export default function ImportWizard() {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null);
  const [fields, setFields] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    API.get("/imports/fields").then((r) => setFields(r.data.fields)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!polling || !job) return;
    const t = setInterval(async () => {
      try {
        const r = await API.get(`/imports/${job.import_id}`);
        setJob((j) => ({ ...j, ...r.data }));
        if (r.data.status === "TERMINE" || r.data.status === "ECHEC") {
          setPolling(false);
          if (r.data.status === "TERMINE") toast.success("Import termine.");
          else toast.error("L'import a echoue. Aucune donnee partielle n'a ete validee.");
        }
      } catch (e) { /* retry au prochain cycle */ }
    }, 1500);
    return () => clearInterval(t);
  }, [polling, job]);

  const doUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await API.post("/imports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJob(r.data);
      setMapping(r.data.suggested_mapping || {});
      setStep(1);
      toast.success(`${r.data.total_rows} ligne(s) detectee(s).`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const doMapping = async () => {
    try {
      const r = await API.post(`/imports/${job.import_id}/mapping`, { mapping });
      setPreview(r.data);
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doCommit = async () => {
    setCommitting(true);
    try {
      await API.post(`/imports/${job.import_id}/commit`);
      setStep(4);
      setPolling(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCommitting(false);
    }
  };

  const restart = () => {
    setStep(0); setFile(null); setJob(null); setMapping({}); setPreview(null); setPolling(false);
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="import-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold flex items-center gap-2">
          Importation de donnees
          <InfoTip testid="tip-import" text="Importez des fichiers Excel (.xlsx, .xls) ou CSV. Une mauvaise importation ne detruit jamais les donnees existantes : les doublons sont detectes et les correspondances incertaines soumises a validation." />
        </h1>
        <p className="text-sm text-muted-foreground">Assistant d'import Excel / CSV avec detection des doublons et rapport telechargeable.</p>
      </div>

      <div className="flex items-center gap-0" data-testid="import-stepper">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center flex-1 last:flex-none">
            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors ${
              i < step ? "bg-[#009E60] border-[#009E60] text-white" :
              i === step ? "border-[#009E60] text-[#009E60]" : "border-border text-muted-foreground"
            }`} data-testid={`step-${i}`}>{i + 1}</div>
            <span className={`ml-2 text-xs font-medium hidden sm:block ${i === step ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-3 ${i < step ? "bg-[#009E60]" : "bg-border"}`} />}
          </div>
        ))}
      </div>

      {step === 0 && (
        <Card data-testid="import-upload-step">
          <CardHeader><CardTitle className="text-base font-heading">1. Choisir le fichier</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <button type="button" onClick={() => fileRef.current?.click()}
              className="w-full rounded-xl border-2 border-dashed border-[#3A75C4]/40 hover:border-[#009E60] bg-accent/30 p-10 text-center transition-colors"
              data-testid="import-dropzone">
              <Upload className="mx-auto h-10 w-10 text-[#3A75C4]" />
              <p className="mt-3 text-sm font-medium">{file ? file.name : "Cliquez pour choisir un fichier"}</p>
              <p className="mt-1 text-xs text-muted-foreground">Formats acceptes : .xlsx, .xls, .csv — 10 Mo max</p>
            </button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
            {file && (
              <div className="flex items-center gap-3 rounded-lg border bg-card p-3">
                <FileSpreadsheet className="h-8 w-8 text-[#009E60]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} Ko</p>
                </div>
                <Button onClick={doUpload} disabled={uploading} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="import-upload-btn">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                  Analyser
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {step === 1 && job && (
        <Card data-testid="import-analysis-step">
          <CardHeader><CardTitle className="text-base font-heading">2. Analyse du fichier</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-heading font-bold text-[#3A75C4]">{job.total_rows}</p>
                <p className="text-xs text-muted-foreground">Lignes detectees</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-heading font-bold text-[#009E60]">{job.columns.length}</p>
                <p className="text-xs text-muted-foreground">Colonnes detectees</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-heading font-bold text-amber-600">{Object.keys(job.suggested_mapping || {}).length}</p>
                <p className="text-xs text-muted-foreground">Mappings suggeres</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Colonnes detectees</p>
              <div className="flex flex-wrap gap-2">
                {job.columns.map((c) => (
                  <span key={c} className="rounded-full bg-accent px-3 py-1 text-xs mono" data-testid={`col-${c}`}>{c}</span>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Apercu (5 premieres lignes)</p>
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-xs">
                  <thead className="bg-accent/60">
                    <tr>{job.columns.map((c) => <th key={c} className="px-2 py-2 text-left font-semibold">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {job.sample_rows.map((r, i) => (
                      <tr key={i} className="border-t">{job.columns.map((c) => <td key={c} className="px-2 py-1.5 max-w-32 truncate">{r[c]}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={restart} data-testid="import-back-0"><ArrowLeft className="h-4 w-4 mr-2" /> Recommencer</Button>
              <Button onClick={() => setStep(2)} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="import-to-mapping">
                Mapper les colonnes <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && job && (
        <Card data-testid="import-mapping-step">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              3. Mapping des colonnes
              <InfoTip testid="tip-mapping" text="Associez chaque colonne du fichier a un champ ALARA. Seule la colonne Nom est obligatoire ; le prenom est facultatif (les proprietaires institutionnels sont importes comme structures)." />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {job.columns.map((col) => (
              <div key={col} className="flex items-center gap-4" data-testid={`mapping-row-${col}`}>
                <span className="w-48 truncate rounded bg-accent px-3 py-2 text-xs mono font-medium">{col}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                <Select value={mapping[col] || "ignore"} onValueChange={(v) => setMapping({ ...mapping, [col]: v === "ignore" ? "" : v })}>
                  <SelectTrigger className="w-64" data-testid={`mapping-select-${col}`}>
                    <SelectValue placeholder="Ignorer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ignore">— Ignorer —</SelectItem>
                    {fields.map((f) => (
                      <SelectItem key={f.key} value={f.key}>{f.label}{f.required ? " *" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
            <div className="flex justify-between pt-3">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="import-back-1"><ArrowLeft className="h-4 w-4 mr-2" /> Retour</Button>
              <Button onClick={doMapping} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="import-preview-btn">
                Previsualiser <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && preview && (
        <Card data-testid="import-preview-step">
          <CardHeader><CardTitle className="text-base font-heading">4. Previsualisation et detection</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-heading font-bold text-[#3A75C4]">{preview.sample_size}</p>
                <p className="text-xs text-muted-foreground">Lignes analysees (echantillon)</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-heading font-bold text-red-600">{preview.errors_in_sample}</p>
                <p className="text-xs text-muted-foreground">Erreurs detectees</p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-2xl font-heading font-bold text-amber-600">{preview.existing_matches_sample}</p>
                <p className="text-xs text-muted-foreground">Correspondances existantes</p>
              </div>
            </div>
            {preview.existing_matches_sample > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200 flex gap-2" data-testid="import-dup-warning">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                Des correspondances avec la base existante ont ete detectees. Les enregistrements identifies par VIN, immatriculation, permis ou telephone seront mis a jour. Les correspondances par nom seul seront signalees comme DOUBLON POTENTIEL et ne seront jamais fusionnees automatiquement.
              </div>
            )}
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-xs">
                <thead className="bg-accent/60">
                  <tr>
                    <th className="px-2 py-2 text-left">Ligne</th>
                    <th className="px-2 py-2 text-left">Nom</th>
                    <th className="px-2 py-2 text-left">Prenom</th>
                    <th className="px-2 py-2 text-left">Permis</th>
                    <th className="px-2 py-2 text-left">Immat.</th>
                    <th className="px-2 py-2 text-left">Erreurs</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((r) => (
                    <tr key={r.ligne} className="border-t" data-testid={`preview-row-${r.ligne}`}>
                      <td className="px-2 py-1.5 mono">{r.ligne}</td>
                      <td className="px-2 py-1.5 font-medium">{r.data.nom || "—"}</td>
                      <td className="px-2 py-1.5">{r.data.prenom || "—"}</td>
                      <td className="px-2 py-1.5 mono">{r.data.numero_permis || "—"}</td>
                      <td className="px-2 py-1.5 mono">{r.data.immatriculation || "—"}</td>
                      <td className="px-2 py-1.5 text-red-600">{r.erreurs.join(", ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="import-back-2"><ArrowLeft className="h-4 w-4 mr-2" /> Retour</Button>
              <Button onClick={doCommit} disabled={committing} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="import-confirm-btn">
                {committing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Confirmer et importer {job.total_rows} lignes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 4 && (
        <Card data-testid="import-result-step">
          <CardHeader><CardTitle className="text-base font-heading">5. Rapport d'importation</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {polling ? (
              <div className="flex flex-col items-center py-8 gap-3" data-testid="import-processing">
                <Loader2 className="h-10 w-10 animate-spin text-[#009E60]" />
                <p className="text-sm text-muted-foreground">Importation en cours...</p>
                {job?.total_rows ? (
                  <>
                    <div className="w-72 h-2.5 rounded-full bg-accent overflow-hidden" data-testid="import-progress-bar">
                      <div className="h-full bg-[#009E60] transition-all duration-500"
                        style={{ width: `${Math.min(100, Math.round(((job.progress || 0) / job.total_rows) * 100))}%` }} />
                    </div>
                    <p className="text-xs text-muted-foreground mono" data-testid="import-progress-text">
                      {(job.progress || 0).toLocaleString("fr-FR")} / {job.total_rows.toLocaleString("fr-FR")} lignes
                    </p>
                  </>
                ) : null}
              </div>
            ) : job?.status ? (
              <>
                <div className="flex items-center gap-3">
                  <StatusBadge value={job.status} testid="import-final-status" />
                  <span className="text-sm text-muted-foreground">{job.filename}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {[
                    ["Total lignes", job.total_rows, "text-[#3A75C4]"],
                    ["Ajoutees", job.added, "text-[#009E60]"],
                    ["Mises a jour", job.updated, "text-blue-600"],
                    ["Doublons", job.duplicates, "text-amber-600"],
                    ["Rejetees", job.rejected, "text-red-600"],
                  ].map(([label, val, color]) => (
                    <div key={label} className="rounded-lg border p-3 text-center" data-testid={`report-${label.toLowerCase().replace(/\s/g, "-")}`}>
                      <p className={`text-2xl font-heading font-bold ${color}`}>{val ?? 0}</p>
                      <p className="text-xs text-muted-foreground">{label}</p>
                    </div>
                  ))}
                </div>
                {(job.errors || []).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Erreurs et doublons ({job.errors.length})</p>
                    <div className="max-h-56 overflow-y-auto rounded-lg border divide-y text-xs">
                      {job.errors.slice(0, 50).map((e, i) => (
                        <div key={i} className="flex gap-3 px-3 py-2" data-testid={`import-error-${i}`}>
                          <span className="mono text-muted-foreground shrink-0">L.{e.ligne}</span>
                          <span className={e.message.includes("CORRESPONDANCE") ? "text-amber-700 dark:text-amber-300" : "text-red-700 dark:text-red-300"}>{e.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-3">
                  <Button variant="outline" asChild data-testid="import-download-report">
                    <a href={exportUrl(`/imports/${job.import_id}/report.csv`)}><Download className="h-4 w-4 mr-2" /> Telecharger le rapport</a>
                  </Button>
                  <Button onClick={restart} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="import-new">
                    Nouvel import
                  </Button>
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
